import React, { useState } from 'react';
import { doc, updateDoc } from 'firebase/firestore';
import { db } from '../firebase';
import { useAuth } from '../contexts/AuthContext';
import { useNavigate } from 'react-router-dom';
import { fileToBase64 } from '../utils/fileUtils';

const STEPS = [
  { id: 1, label: 'Persoonlijk', icon: '👤' },
  { id: 2, label: 'Adres & Contact', icon: '📍' },
  { id: 3, label: 'Kamp Info', icon: '🏕' },
  { id: 4, label: 'Achtergrond', icon: '📋' },
  { id: 5, label: 'Documenten', icon: '📄' },
];

function UploadField({ label, fieldKey, file, onChange, required }) {
  return (
    <div className="field">
      <label>{label} {required && <span className="req">*</span>}</label>
      <label className={`upload-zone ${file ? 'uploaded' : ''}`} style={{ cursor: 'pointer' }}>
        <input type="file" accept=".pdf" style={{ display: 'none' }}
          onChange={e => onChange(fieldKey, e.target.files[0])} />
        {file ? (
          <div>
            <div style={{ fontSize: 24, marginBottom: 4 }}>✅</div>
            <div style={{ fontSize: 13, fontWeight: 500, color: 'var(--primary)' }}>{file.name}</div>
            <div style={{ fontSize: 12, color: 'var(--text-3)', marginTop: 2 }}>Klik om te wijzigen</div>
          </div>
        ) : (
          <div>
            <div style={{ fontSize: 28, marginBottom: 6 }}>📎</div>
            <div style={{ fontSize: 14, fontWeight: 500, color: 'var(--text-2)' }}>Klik om PDF te uploaden</div>
            <div style={{ fontSize: 12, color: 'var(--text-3)', marginTop: 2 }}>Alleen PDF-bestanden (max 500 KB)</div>
          </div>
        )}
      </label>
    </div>
  );
}

function MultiSelect({ label, options, value, onChange, required }) {
  const toggle = (opt) => {
    const arr = value || [];
    onChange(arr.includes(opt) ? arr.filter(v => v !== opt) : [...arr, opt]);
  };
  return (
    <div className="field">
      <label>{label} {required && <span className="req">*</span>}</label>
      <div className="checkbox-group">
        {options.map(opt => (
          <div key={opt} className={`checkbox-pill ${(value || []).includes(opt) ? 'selected' : ''}`}
            onClick={() => toggle(opt)}>
            {opt}
          </div>
        ))}
      </div>
    </div>
  );
}

const MAX_PDF_BYTES = 500 * 1024;

export default function ProfileSetup() {
  const { currentUser, setUserProfile } = useAuth();
  const navigate = useNavigate();
  const [step, setStep] = useState(1);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [files, setFiles] = useState({ cv: null, strafblad: null, contract: null });

  const [form, setForm] = useState({
    voornaam: '', achternaam: '', geboortedatum: '',
    rijksregister: '', rekening: '',
    straat: '', huisnummer: '', postcode: '', gemeente: '',
    telefoon: '',
    noodcontactNaam: '', noodcontactRelatie: '', noodcontactTelefoon: '',
    coachType: [], groep: [], tshirtMaat: '', locatie: [],
    studies: '', hobbys: '',
    reglementGelezen: false,
  });

  function setField(k, v) { setForm(f => ({ ...f, [k]: v })); }

  function setFile(k, f) {
    if (f && f.size > MAX_PDF_BYTES) {
      setError(`"${f.name}" is te groot. Maximum is 500 KB per PDF.`);
      return;
    }
    setError('');
    setFiles(prev => ({ ...prev, [k]: f }));
  }

  function validateStep() {
    const err = (msg) => { setError(msg); return false; };
    setError('');
    if (step === 1) {
      if (!form.voornaam || !form.achternaam) return err('Voornaam en achternaam zijn verplicht.');
      if (!form.geboortedatum) return err('Geboortedatum is verplicht.');
      if (!/^\d{2}\.\d{2}\.\d{2}-\d{3}\.\d{2}$/.test(form.rijksregister))
        return err('Rijksregisternummer moet formaat 00.00.00-000.00 hebben.');
      if (!form.rekening) return err('Rekeningnummer is verplicht.');
    }
    if (step === 2) {
      if (!form.straat || !form.huisnummer || !form.postcode || !form.gemeente)
        return err('Alle adresvelden zijn verplicht.');
      if (!form.telefoon) return err('Telefoonnummer is verplicht.');
      if (!form.noodcontactNaam || !form.noodcontactRelatie || !form.noodcontactTelefoon)
        return err('Alle noodcontactvelden zijn verplicht.');
    }
    if (step === 3) {
      if (!form.coachType.length) return err('Selecteer minstens één coach type.');
      if (!form.groep.length) return err('Selecteer minstens één groep.');
      if (!form.tshirtMaat) return err('T-shirt maat is verplicht.');
      if (!form.locatie.length) return err('Selecteer minstens één locatie.');
    }
    if (step === 4) {
      if (!form.studies) return err('Studies of werkervaring is verplicht.');
    }
    if (step === 5) {
      if (!files.cv) return err('CV (PDF) is verplicht.');
      if (!files.strafblad) return err('Strafblad (PDF) is verplicht.');
      if (!files.contract) return err('Ondertekend vrijwilligerscontract is verplicht.');
      if (!form.reglementGelezen) return err('Je moet bevestigen dat je het intern reglement gelezen hebt.');
    }
    return true;
  }

  async function handleFinish() {
    if (!validateStep()) return;
    setSaving(true);
    try {
      const [cvData, strafbladData, contractData] = await Promise.all([
        fileToBase64(files.cv),
        fileToBase64(files.strafblad),
        fileToBase64(files.contract),
      ]);

      const profileData = {
        ...form,
        cvData,         cvNaam: files.cv.name,
        strafbladData,  strafbladNaam: files.strafblad.name,
        contractData,   contractNaam: files.contract.name,
        profileComplete: true,
        updatedAt: new Date().toISOString(),
      };

      await updateDoc(doc(db, 'users', currentUser.uid), profileData);
      setUserProfile(prev => ({ ...prev, ...profileData }));
      navigate('/dashboard');
    } catch (e) {
      setError('Fout bij opslaan: ' + e.message);
    }
    setSaving(false);
  }

  function next() { if (validateStep()) setStep(s => s + 1); }
  function prev() { setStep(s => s - 1); setError(''); }

  const progress = ((step - 1) / (STEPS.length - 1)) * 100;

  return (
    <div style={{ minHeight: '100vh', background: 'var(--surface-2)', display: 'flex', flexDirection: 'column', alignItems: 'center', padding: '40px 20px' }}>
      <div style={{ textAlign: 'center', marginBottom: 32 }}>
        <div style={{ fontSize: 28, marginBottom: 8 }}>🏕</div>
        <h1 style={{ fontSize: 24, fontWeight: 700, color: 'var(--text-1)', fontFamily: 'Syne, sans-serif' }}>Profiel aanmaken</h1>
        <p style={{ color: 'var(--text-3)', fontSize: 14, marginTop: 4 }}>Vul je gegevens volledig in voor je aan de slag kan</p>
      </div>

      <div style={{ display: 'flex', gap: 8, marginBottom: 28, flexWrap: 'wrap', justifyContent: 'center' }}>
        {STEPS.map(s => (
          <div key={s.id} style={{
            display: 'flex', alignItems: 'center', gap: 6, padding: '6px 14px',
            borderRadius: 100, fontSize: 13, fontWeight: 500,
            background: step === s.id ? 'var(--primary)' : step > s.id ? 'var(--success-pale)' : 'var(--surface)',
            color: step === s.id ? 'white' : step > s.id ? 'var(--primary)' : 'var(--text-3)',
            border: '1px solid', borderColor: step === s.id ? 'var(--primary)' : step > s.id ? 'rgba(27,67,50,0.2)' : 'var(--border)',
          }}>
            <span>{step > s.id ? '✓' : s.icon}</span>{s.label}
          </div>
        ))}
      </div>

      <div style={{ width: '100%', maxWidth: 620, marginBottom: 4 }}>
        <div className="progress-bar"><div className="progress-fill" style={{ width: `${progress}%` }} /></div>
      </div>

      <div className="card card-padded" style={{ width: '100%', maxWidth: 620 }}>
        {error && <div className="alert alert-error" style={{ marginBottom: 20 }}><span>⚠</span> {error}</div>}

        {step === 1 && (
          <div>
            <h2 style={{ marginBottom: 20, fontSize: 18 }}>Persoonlijke gegevens</h2>
            <div className="grid-2">
              <div className="field"><label>Voornaam <span className="req">*</span></label>
                <input className="input" value={form.voornaam} onChange={e => setField('voornaam', e.target.value)} placeholder="Jan" /></div>
              <div className="field"><label>Achternaam <span className="req">*</span></label>
                <input className="input" value={form.achternaam} onChange={e => setField('achternaam', e.target.value)} placeholder="Janssen" /></div>
            </div>
            <div className="field"><label>Geboortedatum <span className="req">*</span></label>
              <input className="input" type="date" value={form.geboortedatum} onChange={e => setField('geboortedatum', e.target.value)} /></div>
            <div className="field"><label>Rijksregisternummer <span className="req">*</span></label>
              <input className="input" value={form.rijksregister} onChange={e => setField('rijksregister', e.target.value)} placeholder="00.00.00-000.00" maxLength={15} />
              <div style={{ fontSize: 12, color: 'var(--text-3)', marginTop: 4 }}>Formaat: 00.00.00-000.00</div></div>
            <div className="field"><label>Rekeningnummer <span className="req">*</span></label>
              <input className="input" value={form.rekening} onChange={e => setField('rekening', e.target.value)} placeholder="BE00 0000 0000 0000" /></div>
          </div>
        )}

        {step === 2 && (
          <div>
            <h2 style={{ marginBottom: 20, fontSize: 18 }}>Adres & Contactgegevens</h2>
            <div className="grid-2" style={{ gridTemplateColumns: '2fr 1fr' }}>
              <div className="field"><label>Straat <span className="req">*</span></label>
                <input className="input" value={form.straat} onChange={e => setField('straat', e.target.value)} placeholder="Kerkstraat" /></div>
              <div className="field"><label>Huisnummer <span className="req">*</span></label>
                <input className="input" value={form.huisnummer} onChange={e => setField('huisnummer', e.target.value)} placeholder="12" /></div>
            </div>
            <div className="grid-2">
              <div className="field"><label>Postcode <span className="req">*</span></label>
                <input className="input" value={form.postcode} onChange={e => setField('postcode', e.target.value)} placeholder="2000" /></div>
              <div className="field"><label>Stad / Gemeente <span className="req">*</span></label>
                <input className="input" value={form.gemeente} onChange={e => setField('gemeente', e.target.value)} placeholder="Antwerpen" /></div>
            </div>
            <div className="field"><label>Telefoonnummer <span className="req">*</span></label>
              <input className="input" value={form.telefoon} onChange={e => setField('telefoon', e.target.value)} placeholder="+32 470 00 00 00" /></div>
            <div className="divider" />
            <h3 style={{ fontSize: 15, fontWeight: 600, marginBottom: 16, color: 'var(--text-2)' }}>🆘 Noodcontact</h3>
            <div className="grid-2">
              <div className="field"><label>Naam <span className="req">*</span></label>
                <input className="input" value={form.noodcontactNaam} onChange={e => setField('noodcontactNaam', e.target.value)} placeholder="Marie Janssen" /></div>
              <div className="field"><label>Relatie <span className="req">*</span></label>
                <select className="input" value={form.noodcontactRelatie} onChange={e => setField('noodcontactRelatie', e.target.value)}>
                  <option value="">Kies relatie...</option>
                  {['Moeder', 'Vader', 'Partner', 'Broer/Zus', 'Vriend/Vriendin', 'Andere'].map(r => <option key={r} value={r}>{r}</option>)}
                </select></div>
            </div>
            <div className="field"><label>Telefoonnummer noodcontact <span className="req">*</span></label>
              <input className="input" value={form.noodcontactTelefoon} onChange={e => setField('noodcontactTelefoon', e.target.value)} placeholder="+32 470 00 00 00" /></div>
          </div>
        )}

        {step === 3 && (
          <div>
            <h2 style={{ marginBottom: 20, fontSize: 18 }}>Kamp informatie</h2>
            <MultiSelect label="Coach type" options={['Stem', 'Cream Sport']} value={form.coachType} onChange={v => setField('coachType', v)} required />
            <MultiSelect label="Groep" options={['Groen', 'Geel', 'Rood', 'Blauw', 'Toekomstheld']} value={form.groep} onChange={v => setField('groep', v)} required />
            <div className="field"><label>T-shirt maat <span className="req">*</span></label>
              <select className="input" value={form.tshirtMaat} onChange={e => setField('tshirtMaat', e.target.value)}>
                <option value="">Kies maat...</option>
                {['XS', 'S', 'M', 'L', 'XL', 'XXL', '3XL'].map(m => <option key={m} value={m}>{m}</option>)}
              </select></div>
            <MultiSelect label="Locatie" options={['Edegem', 'Mortsel', 'Berchem']} value={form.locatie} onChange={v => setField('locatie', v)} required />
          </div>
        )}

        {step === 4 && (
          <div>
            <h2 style={{ marginBottom: 20, fontSize: 18 }}>Achtergrond & vaardigheden</h2>
            <div className="field"><label>Studies of werkervaring <span className="req">*</span></label>
              <textarea className="input" value={form.studies} onChange={e => setField('studies', e.target.value)}
                placeholder="Bijv. Bachelor Toegepaste Psychologie, werkervaring als jeugdwerker..." rows={4} /></div>
            <div className="field"><label>Hobby's en vaardigheden</label>
              <textarea className="input" value={form.hobbys} onChange={e => setField('hobbys', e.target.value)}
                placeholder="Bijv. sporten, muziek, EHBO-diploma, rijbewijs B..." rows={4} /></div>
          </div>
        )}

        {step === 5 && (
          <div>
            <h2 style={{ marginBottom: 20, fontSize: 18 }}>Documenten uploaden</h2>
            <div className="alert alert-warning" style={{ marginBottom: 20 }}>
              <span>ℹ</span>
              <span>PDF-bestanden worden opgeslagen in Firestore. <strong>Maximum 500 KB per bestand.</strong></span>
            </div>
            <UploadField label="CV" fieldKey="cv" file={files.cv} onChange={setFile} required />
            <UploadField label="Uittreksel uit het strafregister" fieldKey="strafblad" file={files.strafblad} onChange={setFile} required />
            <UploadField label="Ondertekend vrijwilligerscontract" fieldKey="contract" file={files.contract} onChange={setFile} required />
            <div className="divider" />
            <div style={{ display: 'flex', alignItems: 'flex-start', gap: 12, padding: 16, background: 'var(--warning-pale)', borderRadius: 'var(--radius-sm)', border: '1px solid rgba(230,126,34,0.2)' }}>
              <input type="checkbox" id="reglement" checked={form.reglementGelezen}
                onChange={e => setField('reglementGelezen', e.target.checked)}
                style={{ width: 18, height: 18, marginTop: 2, cursor: 'pointer', accentColor: 'var(--primary)', flexShrink: 0 }} />
              <label htmlFor="reglement" style={{ fontSize: 14, cursor: 'pointer', color: 'var(--text-1)', marginBottom: 0 }}>
                <strong>Ik bevestig dat ik het intern reglement gelezen en begrepen heb</strong>
                <span style={{ display: 'block', fontSize: 12, color: 'var(--text-3)', marginTop: 2 }}>Het intern reglement is beschikbaar via de admin of op aanvraag.</span>
              </label>
            </div>
          </div>
        )}

        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginTop: 28, paddingTop: 20, borderTop: '1px solid var(--border)' }}>
          <button className="btn btn-secondary" onClick={prev} disabled={step === 1}>← Vorige</button>
          <span style={{ fontSize: 13, color: 'var(--text-3)' }}>{step} / {STEPS.length}</span>
          {step < STEPS.length
            ? <button className="btn btn-primary" onClick={next}>Volgende →</button>
            : <button className="btn btn-primary btn-lg" onClick={handleFinish} disabled={saving}>
                {saving ? 'Opslaan...' : '✓ Profiel opslaan'}
              </button>
          }
        </div>
      </div>
    </div>
  );
}
